import { useState, useCallback, useMemo } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Calculator, Search, Coins, Building2, Scale, Heart, Info, Home, Clock, CalendarDays } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import SubpageHero from '@/components/common/SubpageHero'
import Button from '@/components/ui/Button'
import Spinner from '@/components/ui/Spinner'
import CostResultCard from '@/components/cost/CostResultCard'
import CostBreakdown from '@/components/cost/CostBreakdown'
import CostReferenceNotes from '@/components/cost/CostReferenceNotes'
import NonCoveredItems from '@/components/cost/NonCoveredItems'
import ReductionInfo from '@/components/cost/ReductionInfo'
import CostSimulatorFaq from '@/components/cost/CostSimulatorFaq'
import { useCalculateCost } from '@/features/cost/api'
import { toast } from '@/components/ui/Toast'
import { cn } from '@/lib/utils'
import { ROUTES } from '@/lib/constants'
import { trackCalculateCost } from '@/lib/analytics'
import type { CareGrade } from '@/types/common'
import type {
  ReductionType,
  FacilitySize,
  CostSimulationRequest,
  CostSimulationResponse,
} from '@/types/cost'
import { REDUCTION_TYPE_LABELS, REDUCTION_TYPE_HINTS, benefitKindOf } from '@/types/cost'
import SEO from '@/components/common/SEO'
import PromotionSlot from '@/components/common/PromotionSlot'
import StickySideBanner from '@/components/common/StickySideBanner'

/* -- Zod 스키마 -- */
const costFormSchema = z.object({
  careGrade: z.enum(['1', '2', '3', '4', '5', 'COGNITIVE'], {
    required_error: '요양등급을 선택해주세요.',
  }),
  facilityType: z.enum(['NURSING_HOME', 'DAY_CARE', 'HOME_VISIT_CARE'], {
    required_error: '시설 유형을 선택해주세요.',
  }),
  facilitySize: z.enum(['LARGE', 'SMALL']),
  reductionType: z.enum(['NONE', 'BASIC_LIVELIHOOD', 'NEAR_POVERTY_1', 'NEAR_POVERTY_2']),
  includeNonCovered: z.boolean(),
  daysPerMonth: z.number().min(1).max(31).optional(),
  visitDuration: z.enum(['30MIN', '60MIN', '90MIN', '120MIN', '150MIN', '180MIN', '210MIN', '240MIN']).optional(),
})

type CostFormValues = z.infer<typeof costFormSchema>

/* -- 등급 옵션 -- */
const careGradeOptions: { value: CareGrade; label: string; description: string }[] = [
  { value: '1', label: '1등급', description: '최중증' },
  { value: '2', label: '2등급', description: '중증' },
  { value: '3', label: '3등급', description: '중등증' },
  { value: '4', label: '4등급', description: '경증' },
  { value: '5', label: '5등급', description: '경증' },
  { value: 'COGNITIVE', label: '인지지원', description: '인지' },
]

/* -- 시설 유형 옵션 -- */
const facilityTypeOptions: { value: 'NURSING_HOME' | 'DAY_CARE' | 'HOME_VISIT_CARE'; label: string; icon: typeof Building2 }[] = [
  { value: 'NURSING_HOME', label: '요양원', icon: Building2 },
  { value: 'DAY_CARE', label: '주간보호', icon: Heart },
  { value: 'HOME_VISIT_CARE', label: '방문요양', icon: Home },
]


/* -- 시설 규모 옵션 -- */
const facilitySizeOptions: { value: FacilitySize; label: string }[] = [
  { value: 'LARGE', label: '10인 이상' },
  { value: 'SMALL', label: '10인 미만' },
]

/* -- 시설유형별 허용 등급 --
 * 요양원(시설급여)은 2026년 고시상 3·4·5등급이 동일 수가이며, 4·5등급도
 * 등급판정위원회가 시설급여 필요를 인정하면 이용할 수 있다. 인지지원등급은
 * 시설급여 대상이 아니라 제외한다. */
const FACILITY_GRADE_MAP: Record<string, Set<string>> = {
  NURSING_HOME: new Set(['1', '2', '3', '4', '5']),
  DAY_CARE: new Set(['1', '2', '3', '4', '5', 'COGNITIVE']),
  HOME_VISIT_CARE: new Set(['1', '2', '3', '4', '5', 'COGNITIVE']),
}

/** 시설급여 이용에 등급판정위원회 인정이 필요한 등급 */
const FACILITY_CONDITIONAL_GRADES = new Set(['3', '4', '5'])

/* -- 방문 시간 옵션 (HOME_VISIT_CARE) -- */
const visitDurationOptions: { value: string; label: string }[] = [
  { value: '60MIN', label: '1시간' },
  { value: '90MIN', label: '1.5시간' },
  { value: '120MIN', label: '2시간' },
  { value: '150MIN', label: '2.5시간' },
  { value: '180MIN', label: '3시간' },
  { value: '240MIN', label: '4시간' },
]

/* -- 감경 유형 옵션 --
 * 라벨·대상자 문구는 types/cost.ts 한 곳에서 온다 (구현지침 §7.2).
 * enum 값은 백엔드 계약이라 그대로 두고 표시 문구만 공식 구분을 따른다. */
const reductionTypeOptions: { value: ReductionType; label: string; hint: string }[] = (
  ['NONE', 'BASIC_LIVELIHOOD', 'NEAR_POVERTY_1', 'NEAR_POVERTY_2'] as const
).map((value) => ({ value, label: REDUCTION_TYPE_LABELS[value], hint: REDUCTION_TYPE_HINTS[value] }))

/**
 * 비용 시뮬레이터 페이지 v2.0
 *
 * - 등급 선택 버튼: 더 크고 선택감 있는 디자인
 * - 결과 카드: 그라데이션 배경 + 큰 숫자 강조
 * - 페이지 진입 애니메이션
 */
export default function CostSimulatorPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const calculateCost = useCalculateCost()
  const [result, setResult] = useState<CostSimulationResponse | null>(null)

  // 시설별 비용 계산 모드
  const facilityId = useMemo(() => {
    const id = searchParams.get('facilityId')
    return id ? Number(id) : undefined
  }, [searchParams])
  const facilityName = searchParams.get('facilityName') || undefined
  const facilityTypeParam = searchParams.get('facilityType') || undefined
  const capacityParam = Number(searchParams.get('capacity') || '0')

  // 시설 유형 매핑: 백엔드 FacilityType → 비용 시뮬레이터 유형
  const mappedFacilityType = useMemo(() => {
    if (facilityTypeParam === 'NURSING_HOME' || facilityTypeParam === 'NURSING_HOME_SMALL' || facilityTypeParam === 'SHORT_STAY') {
      return 'NURSING_HOME' as const
    }
    if (facilityTypeParam === 'DAY_CARE' || facilityTypeParam === 'DEMENTIA_CARE') {
      return 'DAY_CARE' as const
    }
    if (facilityTypeParam === 'HOME_VISIT_CARE') return 'HOME_VISIT_CARE' as const
    return undefined
  }, [facilityTypeParam])

  const mappedFacilitySize = useMemo(() => {
    if (facilityTypeParam === 'NURSING_HOME_SMALL') return 'SMALL' as const
    if (capacityParam > 0) return capacityParam >= 10 ? 'LARGE' as const : 'SMALL' as const
    return 'LARGE' as const
  }, [facilityTypeParam, capacityParam])

  // 미지원 시설 유형 여부
  // isUnsupportedType removed: unused (reserved for future use)

  const {
    control,
    handleSubmit,
    watch,
    getValues,
    setValue,
    formState: { errors },
  } = useForm<CostFormValues>({
    resolver: zodResolver(costFormSchema),
    defaultValues: {
      facilityType: mappedFacilityType,
      facilitySize: mappedFacilitySize as 'LARGE' | 'SMALL',
      reductionType: 'NONE',
      includeNonCovered: true,
      daysPerMonth: 20,
      visitDuration: '120MIN',
    },
  })

  const watchReductionType = watch('reductionType') as ReductionType
  const watchIncludeNonCovered = watch('includeNonCovered')
  const watchFacilityType = watch('facilityType')
  const watchCareGrade = watch('careGrade')

  // 시설유형별 허용 등급 필터링
  const filteredGradeOptions = useMemo(() => {
    const allowed = FACILITY_GRADE_MAP[watchFacilityType] ?? new Set(['1', '2', '3', '4', '5', 'COGNITIVE'])
    return careGradeOptions.filter((o) => allowed.has(o.value))
  }, [watchFacilityType])

  const onSubmit = useCallback(
    (data: CostFormValues) => {
      // 시설유형별 daysPerMonth 및 facilitySize 분기
      const daysPerMonth =
        data.facilityType === 'NURSING_HOME' ? 30
        : (data.daysPerMonth ?? (data.facilityType === 'DAY_CARE' ? 20 : 12))

      const facilitySize: FacilitySize =
        data.facilityType === 'NURSING_HOME' ? (data.facilitySize as FacilitySize)
        : data.facilityType === 'DAY_CARE' ? 'DEFAULT'
        : ((data.visitDuration ?? '120MIN') as FacilitySize)

      const request: CostSimulationRequest = {
        careGrade: data.careGrade as CareGrade,
        facilityType: data.facilityType,
        daysPerMonth,
        reductionType: data.reductionType as ReductionType,
        facilitySize,
        includeNonCovered: data.includeNonCovered,
        facilityId,
      }

      calculateCost.mutate(request, {
        onSuccess: (response) => {
          setResult(response)
          trackCalculateCost(data.facilityType, response.totalEstimate?.min, response.totalEstimate?.max)
          setTimeout(() => {
            document.getElementById('cost-result')?.scrollIntoView({
              behavior: 'smooth',
              block: 'start',
            })
          }, 100)
        },
        onError: () => {
          toast.error('비용 계산에 실패했어요. 다시 시도해주세요.')
        },
      })
    },
    [calculateCost]
  )

  const handleFindFacility = () => {
    const formValues = getValues()
    const params = new URLSearchParams()

    if (formValues.facilityType) {
      params.set('facilityType', formValues.facilityType)
    }
    if (formValues.careGrade) {
      params.set('careGrade', `GRADE_${formValues.careGrade}`)
    }
    if (result) {
      params.set('copayAmount', String(result.copayAmount))
      params.set('totalMin', String(result.totalEstimate.min))
      params.set('totalMax', String(result.totalEstimate.max))
    }
    if (formValues.reductionType && formValues.reductionType !== 'NONE') {
      params.set('reductionType', formValues.reductionType)
    }

    navigate(`${ROUTES.FACILITIES}?${params.toString()}`)
  }

  /* -- 공통 JSX: 시설별 비용 계산 모드 배너 -- */
  const facilityBanner = facilityName && (
    <div className="flex items-start gap-3 rounded-2xl bg-cb-primary-light border border-cb-primary-muted p-4">
      <Info size={20} className="text-cb-primary flex-shrink-0 mt-0.5" />
      <div>
        <p className="text-body-sm font-bold text-cb-primary">
          {facilityName}
        </p>
        <p className="text-caption text-cb-gray-600 mt-0.5">
          {facilityId
            ? '이 시설의 실제 비급여 데이터가 반영됩니다'
            : '전국 평균 비급여 비용으로 계산됩니다'}
        </p>
      </div>
    </div>
  )

  /* -- 공통 JSX: 입력 폼 -- */
  const formContent = (
    <form onSubmit={handleSubmit(onSubmit)} noValidate>
      <div className="flex flex-col gap-6">
        {/* 요양등급 선택 (필수) - 더 크고 선택감 있는 디자인 */}
        <fieldset>
          <legend className="text-body font-bold text-cb-gray-900 mb-3 flex items-center gap-2">
            <Coins size={16} className="text-cb-primary" />
            요양등급 <span className="text-cb-danger">*</span>
          </legend>
          <Controller
            name="careGrade"
            control={control}
            render={({ field }) => (
              <div className="grid grid-cols-3 gap-2.5">
                {filteredGradeOptions.map((option) => {
                  const isSelected = field.value === option.value
                  return (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => field.onChange(option.value)}
                      aria-pressed={isSelected}
                      aria-label={`요양등급 ${option.label}`}
                      className={cn(
                        'flex flex-col items-center justify-center rounded-2xl border-2 py-3.5 gap-0.5 transition-all duration-200',
                        'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cb-primary',
                        isSelected
                          ? 'border-cb-primary bg-cb-primary-light text-cb-primary shadow-md shadow-cb-primary/15 scale-[1.02]'
                          : 'border-cb-gray-200 bg-white text-cb-gray-700 hover:border-cb-gray-300 hover:bg-cb-gray-50'
                      )}
                    >
                      <span className="text-body font-bold">{option.label}</span>
                      <span className={cn('text-[10px]', isSelected ? 'text-cb-primary/70' : 'text-cb-gray-400')}>
                        {option.description}
                      </span>
                    </button>
                  )
                })}
              </div>
            )}
          />
          {errors.careGrade && (
            <p role="alert" className="mt-2 text-caption text-cb-danger font-medium">
              {errors.careGrade.message}
            </p>
          )}
          {/* 요양원은 1·2등급이 원칙이고 3~5등급은 별도 인정이 필요하다 — 오해 방지 */}
          {watchFacilityType === 'NURSING_HOME' &&
            FACILITY_CONDITIONAL_GRADES.has(watchCareGrade ?? '') && (
              <p className="mt-2 text-caption text-cb-gray-500 leading-relaxed">
                요양원 입소는 1·2등급이 원칙이며, 3~5등급은 등급판정위원회가 시설급여가 필요하다고
                인정한 경우에 이용하실 수 있어요.
              </p>
            )}
        </fieldset>

        {/* 시설 유형 (필수) - 아이콘 포함 */}
        <fieldset>
          <legend className="text-body font-bold text-cb-gray-900 mb-3 flex items-center gap-2">
            <Building2 size={16} className="text-cb-primary" />
            시설 유형 <span className="text-cb-danger">*</span>
          </legend>
          <Controller
            name="facilityType"
            control={control}
            render={({ field }) => (
              <div className="grid grid-cols-3 gap-2.5">
                {facilityTypeOptions.map((option) => {
                  const isSelected = field.value === option.value
                  const Icon = option.icon
                  return (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => {
                        field.onChange(option.value)
                        // 시설유형 변경 시: 현재 선택된 등급이 새 유형에서 허용되지 않으면 초기화
                        const currentGrade = getValues('careGrade')
                        const allowed = FACILITY_GRADE_MAP[option.value]
                        if (currentGrade && allowed && !allowed.has(currentGrade)) {
                          setValue('careGrade', undefined as unknown as '1')
                        }
                        setResult(null)
                      }}
                      aria-pressed={isSelected}
                      aria-label={`시설 유형 ${option.label}`}
                      className={cn(
                        'flex flex-col items-center justify-center gap-1.5 rounded-2xl border-2 py-3.5 transition-all duration-200',
                        'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cb-primary',
                        isSelected
                          ? 'border-cb-primary bg-cb-primary-light text-cb-primary shadow-md shadow-cb-primary/15'
                          : 'border-cb-gray-200 bg-white text-cb-gray-700 hover:border-cb-gray-300 hover:bg-cb-gray-50'
                      )}
                    >
                      <Icon size={20} />
                      <span className="text-body-sm font-bold">{option.label}</span>
                    </button>
                  )
                })}
              </div>
            )}
          />
          {errors.facilityType && (
            <p role="alert" className="mt-2 text-caption text-cb-danger font-medium">
              {errors.facilityType.message}
            </p>
          )}
        </fieldset>

        {/* 시설 규모 — 요양원만 표시 */}
        {watchFacilityType === 'NURSING_HOME' && (
          <fieldset>
            <legend className="text-body font-bold text-cb-gray-900 mb-3 flex items-center gap-2">
              <Scale size={16} className="text-cb-gray-500" />
              시설 규모
            </legend>
            <Controller
              name="facilitySize"
              control={control}
              render={({ field }) => (
                <div className="grid grid-cols-2 gap-3">
                  {facilitySizeOptions.map((option) => {
                    const isSelected = field.value === option.value
                    return (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => field.onChange(option.value)}
                        aria-pressed={isSelected}
                        className={cn(
                          'flex items-center justify-center rounded-2xl border-2 py-3 text-body-sm font-semibold transition-all duration-200',
                          'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cb-primary',
                          isSelected
                            ? 'border-cb-primary bg-cb-primary-light text-cb-primary shadow-sm'
                            : 'border-cb-gray-200 bg-white text-cb-gray-700 hover:border-cb-gray-300 hover:bg-cb-gray-50'
                        )}
                      >
                        {option.label}
                      </button>
                    )
                  })}
                </div>
              )}
            />
          </fieldset>
        )}

        {/* 1회 방문시간 — 방문요양만 표시 */}
        {watchFacilityType === 'HOME_VISIT_CARE' && (
          <fieldset>
            <legend className="text-body font-bold text-cb-gray-900 mb-3 flex items-center gap-2">
              <Clock size={16} className="text-cb-gray-500" />
              1회 방문시간
            </legend>
            <Controller
              name="visitDuration"
              control={control}
              render={({ field }) => (
                <div className="grid grid-cols-3 gap-2.5">
                  {visitDurationOptions.map((option) => {
                    const isSelected = field.value === option.value
                    return (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => field.onChange(option.value)}
                        aria-pressed={isSelected}
                        className={cn(
                          'flex items-center justify-center rounded-2xl border-2 py-3 text-body-sm font-semibold transition-all duration-200',
                          'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cb-primary',
                          isSelected
                            ? 'border-cb-primary bg-cb-primary-light text-cb-primary shadow-sm'
                            : 'border-cb-gray-200 bg-white text-cb-gray-700 hover:border-cb-gray-300 hover:bg-cb-gray-50'
                        )}
                      >
                        {option.label}
                      </button>
                    )
                  })}
                </div>
              )}
            />
          </fieldset>
        )}

        {/* 월 이용일수/방문횟수 — 주간보호/방문요양만 표시 */}
        {(watchFacilityType === 'DAY_CARE' || watchFacilityType === 'HOME_VISIT_CARE') && (
          <fieldset>
            <legend className="text-body font-bold text-cb-gray-900 mb-3 flex items-center gap-2">
              <CalendarDays size={16} className="text-cb-gray-500" />
              {watchFacilityType === 'DAY_CARE' ? '월 이용일수' : '월 방문횟수'}
            </legend>
            <Controller
              name="daysPerMonth"
              control={control}
              render={({ field }) => (
                <div className="flex items-center gap-4">
                  <input
                    type="range"
                    min={1}
                    max={31}
                    value={field.value ?? (watchFacilityType === 'DAY_CARE' ? 20 : 12)}
                    onChange={(e) => field.onChange(Number(e.target.value))}
                    className="flex-1 h-2 bg-cb-gray-200 rounded-lg appearance-none cursor-pointer accent-cb-primary"
                    aria-label={watchFacilityType === 'DAY_CARE' ? '월 이용일수' : '월 방문횟수'}
                  />
                  <span className="w-20 text-center text-body font-bold text-cb-primary bg-cb-primary-light rounded-xl py-2">
                    {field.value ?? (watchFacilityType === 'DAY_CARE' ? 20 : 12)}
                    {watchFacilityType === 'DAY_CARE' ? '일' : '회'}
                  </span>
                </div>
              )}
            />
            <p className="text-caption text-cb-gray-400 mt-2">
              {watchFacilityType === 'DAY_CARE'
                ? '주 5일 기준 약 20일이 일반적입니다'
                : '주 3회 기준 약 12회가 일반적입니다'}
            </p>
          </fieldset>
        )}

        {/* 감경 유형 */}
        <fieldset>
          <legend className="text-body font-bold text-cb-gray-900 mb-3">
            감경 유형
          </legend>
          <Controller
            name="reductionType"
            control={control}
            render={({ field }) => (
              <div className="grid grid-cols-2 gap-2.5">
                {reductionTypeOptions.map((option) => {
                  const isSelected = field.value === option.value
                  return (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => field.onChange(option.value)}
                      aria-pressed={isSelected}
                      className={cn(
                        'flex flex-col items-center justify-center gap-0.5 rounded-2xl border-2 px-2 py-3 text-center transition-all duration-200',
                        'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cb-primary',
                        isSelected
                          ? 'border-cb-primary bg-cb-primary-light text-cb-primary shadow-sm'
                          : 'border-cb-gray-200 bg-white text-cb-gray-700 hover:border-cb-gray-300 hover:bg-cb-gray-50'
                      )}
                    >
                      <span className="text-body-sm font-semibold">{option.label}</span>
                      <span className={cn('text-[11px] leading-snug', isSelected ? 'text-cb-primary/70' : 'text-cb-gray-400')}>
                        {option.hint}
                      </span>
                    </button>
                  )
                })}
              </div>
            )}
          />
          <p className="mt-2 text-caption text-cb-gray-400 leading-relaxed">
            감경을 받아도 식재료비 같은 비급여는 전액 본인부담이에요.
          </p>
        </fieldset>

        {/* 비급여 포함 여부 */}
        <div className="flex items-center justify-between rounded-2xl bg-cb-gray-50 p-4">
          <div className="flex flex-col">
            <span className="text-body font-bold text-cb-gray-900">
              비급여 항목 포함
            </span>
            <span className="text-caption text-cb-gray-500 mt-0.5">
              {facilityId ? '이 시설의 실제 비급여 비용이 반영됩니다' : '식재료비, 간식비, 이미용비 등 (전국 평균)'}
            </span>
          </div>
          <Controller
            name="includeNonCovered"
            control={control}
            render={({ field }) => (
              <button
                type="button"
                role="switch"
                aria-checked={field.value}
                aria-label="비급여 항목 포함"
                onClick={() => field.onChange(!field.value)}
                className={cn(
                  'relative inline-flex h-7 w-12 items-center rounded-full transition-colors duration-200',
                  'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cb-primary',
                  field.value ? 'bg-cb-primary' : 'bg-cb-gray-300'
                )}
              >
                <span
                  className={cn(
                    'inline-block h-5 w-5 transform rounded-full bg-white shadow-sm transition-transform duration-200',
                    field.value ? 'translate-x-6' : 'translate-x-1'
                  )}
                />
              </button>
            )}
          />
        </div>

        {/* 계산 버튼 */}
        <Button
          type="submit"
          size="lg"
          fullWidth
          loading={calculateCost.isPending}
          aria-label="비용 계산하기"
        >
          비용 계산하기
        </Button>
      </div>
    </form>
  )

  /* -- 공통 JSX: 계산 결과 -- */
  const resultContent = (
    <AnimatePresence>
      {result && (
        <motion.div
          id="cost-result"
          className="flex flex-col gap-4 scroll-mt-4"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, ease: 'easeOut' }}
        >
          {/* 월 예상 비용 (큰 카드) */}
          <CostResultCard totalEstimate={result.totalEstimate} />

          {/* 비용 상세 */}
          <CostBreakdown
            result={result}
            includeNonCovered={watchIncludeNonCovered}
          />

          {/* 보조 안내 — 배치 미만 시설 참고 금액·중증 가산. 주 금액은 바꾸지 않는다 (지침 §7.6) */}
          <CostReferenceNotes result={result} />

          {/* 비급여 항목 상세 — 백엔드가 nonCoveredEstimate=null 로 응답할 수 있음 (includeNonCovered=false 또는 데이터 부재) */}
          {watchIncludeNonCovered &&
            result.nonCoveredEstimate &&
            result.nonCoveredEstimate.items.length > 0 && (
              <>
                {facilityId && (
                  <div className="flex items-center gap-2 px-1">
                    <div className="h-2 w-2 rounded-full bg-cb-secondary" />
                    <span className="text-caption text-cb-secondary font-semibold">실제 시설 비급여 데이터 반영</span>
                  </div>
                )}
                <NonCoveredItems items={result.nonCoveredEstimate.items} />
              </>
            )}

          {/* 감경 안내 */}
          <ReductionInfo
            reductionType={watchReductionType}
            benefitKind={benefitKindOf(watchFacilityType)}
          />

          {/* 시설 찾기 CTA */}
          <Button
            variant="secondary"
            size="lg"
            fullWidth
            onClick={handleFindFacility}
            aria-label="이 조건으로 시설 검색하기"
          >
            <Search size={18} aria-hidden="true" />
            이 조건으로 시설 검색하기
          </Button>
        </motion.div>
      )}
    </AnimatePresence>
  )

  /* -- 공통 JSX: 로딩 중 -- */
  const loadingContent = calculateCost.isPending && !result && (
    <div className="flex flex-col items-center gap-3 py-8">
      <Spinner size="lg" />
      <p className="text-body text-cb-gray-500">비용을 계산하고 있어요...</p>
    </div>
  )

  /* ===== 통합 레이아웃 — 데스크톱/모바일 분기 트리 swap 제거 (removeChild 에러 방지) ===== */
  // SSR 시 모바일 트리, 클라 마운트 시 데스크톱 트리로 갈아끼우면 React reconciler 가
  // 외부에서 손댄 노드(번역 확장·AdSense·extension content script 등)를 찾지 못해
  // removeChild NotFoundError 발생. Layout 과 동일한 방식으로 단일 트리 + Tailwind lg: 토글.
  return (
    <>
      {/* 요양비용 계산기 좌우 세로배너(160x600) — 콘텐츠(max-w-6xl=1152px) 양옆 여백에.
          2xl(1536px↑) 초광폭에서만 노출, 푸터 위에서 멈춤(StickySideBanner). */}
      <StickySideBanner slotKey="simulator_side_left" side="left" topPx={256} />
      <StickySideBanner slotKey="simulator_side_right" side="right" topPx={256} />
    <main className="flex-1">
      {/* 검색어: 요양원 비용 계산·본인부담금·감경 — /care-calculator(등급 판정)와 겹치지 않게 (구현지침 §7.4) */}
      <SEO
        title="요양원 비용 계산기 - 등급별 본인부담금·감경 계산"
        description="요양원·주간보호·방문요양 한 달 비용을 계산해 보세요. 등급별 본인부담금에 감경(면제·60%·40%)과 식재료비 같은 비급여까지 더해 보여드려요."
        url="/cost-simulator"
      />

      {/* 데스크톱 Hero (모바일은 자체 헤더 노출) */}
      <div className="hidden lg:block">
        <SubpageHero
          title="요양비용 계산기"
          description="등급별 본인부담금부터 비급여 항목까지 예상 비용을 확인하세요"
          icon={<Calculator size={20} className="text-white" />}
        />
      </div>

      <motion.div
        className="mx-auto w-full max-w-[480px] px-[var(--spacing-page-x)] py-[var(--spacing-section)] lg:max-w-6xl lg:px-6 lg:py-6"
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        {/* 모바일 전용 헤더 — h1 은 SubpageHero 하나뿐이다. CSS 로 숨겨도 SSR HTML 에는 둘 다 남으므로
            여기는 h2 (클래스 그대로라 모양 같음, ssr-spec §17.4.4) */}
        <section className="mb-[var(--spacing-section)] lg:hidden">
          <div className="mb-2 flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-gradient-to-br from-amber-100 to-orange-100 shadow-sm">
              <Calculator size={22} className="text-cb-accent" aria-hidden="true" />
            </div>
            <h2 className="text-h1 font-bold text-cb-gray-900">요양비용 계산기</h2>
          </div>
          <p className="text-body text-cb-gray-500">
            요양 비용이 궁금하세요? 등급과 시설 유형만 선택하면 예상 비용을 알려드려요
          </p>
        </section>

        {/* 콘텐츠: 모바일 1단 / 데스크톱 좌측 폼 + 우측 sticky 결과 */}
        <div className="flex flex-col gap-[var(--spacing-section)] lg:flex-row lg:gap-8">
          {/* 입력 폼 (좌) */}
          <div className="flex-1 space-y-6">
            {facilityBanner}
            {formContent}
          </div>

          {/* 결과 (모바일: 폼 아래 풀 폭 / 데스크톱: 우측 400px sticky) */}
          <div className="lg:w-[400px] lg:flex-shrink-0">
            <div className="space-y-4 lg:sticky lg:top-20">
              {result ? (
                <>
                  {resultContent}
                  {loadingContent}
                </>
              ) : (
                <>
                  {loadingContent || (
                    <div className="hidden rounded-2xl border border-cb-gray-200 bg-cb-gray-50 p-8 text-center lg:block">
                      <Calculator size={48} className="mx-auto mb-4 text-cb-gray-300" />
                      <p className="text-body-sm text-cb-gray-400">
                        조건을 선택하고 계산하기를<br />눌러주세요
                      </p>
                    </div>
                  )}
                </>
              )}
              {/* 프로모션 슬롯: simulator_result_side — 계산 전/후 항상 노출(빈 슬롯이면 애드핏) */}
              <PromotionSlot slotKey="simulator_result_side" className="mt-2" />
            </div>
          </div>
        </div>

        {/* 설명 문단 + FAQ — 2단 레이아웃 아래 전체 폭. 계산 전에도 항상 렌더(SSR 본문, 구현지침 §7.3) */}
        <div className="mt-10 border-t border-cb-gray-100 pt-8 lg:mt-12">
          <CostSimulatorFaq />
        </div>
      </motion.div>
    </main>
    </>
  )
}
